import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ShieldCheck, ShieldAlert, AlertTriangle, History, Loader2 } from 'lucide-react';
import logger from '../../utils/logger';

// Compute Sun-Sat week start for a given JS Date.
function getSundayStart(d = new Date()) {
  const date = new Date(d);
  const day = date.getDay(); // 0 = Sun
  date.setDate(date.getDate() - day);
  return date.toISOString().slice(0, 10);
}

const SERVICE_COLORS = {
  day_program: '#6B3FA0',
  in_home: '#43A047',
  employment: '#E65100',
};

function UtilBar({ pct, over }) {
  const safe = Math.min(100, Math.max(0, pct || 0));
  const color = over ? '#D9534F' : pct >= 85 ? '#E65100' : pct >= 60 ? '#FF9800' : '#43A047';
  return (
    <div className="h-2 rounded-full bg-[#EDE7F6] overflow-hidden w-full">
      <div className="h-full rounded-full" style={{ width: `${safe}%`, background: color }} />
    </div>
  );
}

export default function ComplianceTab({ api }) {
  const [weekStart, setWeekStart] = useState(getSundayStart());
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, oRes] = await Promise.all([
        api.get(`/compliance/participants?week_start=${weekStart}`),
        api.get(`/compliance/overrides?limit=50&week_start=${weekStart}`),
      ]);
      setData(dRes.data);
      setOverrides(oRes.data?.overrides || []);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api, weekStart]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || { total: 0, over: 0, near_limit: 0, compliant: 0 };
  const participants = data?.participants || [];

  return (
    <div className="space-y-5" data-testid="compliance-tab">
      {/* Header / week picker */}
      <Card className="border-[#DED8EB] bg-white">
        <CardContent className="p-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#6B3FA0]" />
            <div>
              <h2 className="text-base font-bold text-[#2D1B4E]">Authorization Compliance</h2>
              <p className="text-xs text-[#5A4D6E]">
                Tracks weekly authorized hours/units per participant for IHCS, Employment, and Explore Program.
                Week runs <strong>Sunday → Saturday</strong>.
              </p>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8E85A0]">Week Starting (Sun)</Label>
              <Input type="date" value={weekStart} onChange={e => setWeekStart(getSundayStart(new Date(e.target.value + 'T12:00')))}
                className="border-[#DED8EB] w-44" data-testid="compliance-week-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Tracked Participants" value={summary.total} accent="#6B3FA0" testid="stat-total" />
        <StatCard label="Over Authorization" value={summary.over} accent="#D9534F" critical={summary.over > 0} testid="stat-over" />
        <StatCard label="Near Limit (≥85%)" value={summary.near_limit} accent="#E65100" testid="stat-near" />
        <StatCard label="Compliant" value={summary.compliant} accent="#43A047" testid="stat-ok" />
      </div>

      {/* Overrides this week */}
      {overrides.length > 0 && (
        <Card className="border-[#D9534F]/40 bg-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-[#D9534F]" />
              <h3 className="text-sm font-bold text-[#2D1B4E]">Recent Overrides ({overrides.length})</h3>
              <Badge className="bg-[#FDECEA] text-[#D9534F] border border-[#D9534F]/40 text-[10px]">Audit Log</Badge>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {overrides.map(o => (
                <div key={o.id} className="border border-[#DED8EB] rounded-md p-3 text-xs" data-testid={`override-row-${o.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-[#2D1B4E]">
                        {(o.breaches || []).map(b => `${b.participant_name} · ${b.service_label}`).join(' · ')}
                      </p>
                      <p className="text-[#5A4D6E] mt-0.5">
                        Logged by <strong>{o.created_by_name || '—'}</strong> · {new Date(o.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge className="bg-[#FDECEA] text-[#D9534F] border border-[#D9534F] text-[10px] shrink-0">Override</Badge>
                  </div>
                  <p className="mt-2 text-[#5A4D6E] italic">"{o.reason || '—'}"</p>
                  <div className="mt-1.5 flex flex-wrap gap-2 text-[10px] font-mono text-[#8E85A0]">
                    {(o.breaches || []).map((b, i) => (
                      <span key={i}>+{b.overage_hours}h ({(b.overage_hours * 4).toFixed(1)}u) over {b.auth_hours}h auth</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participant list */}
      <Card className="border-[#DED8EB] bg-white">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-[#2D1B4E] mb-3">Participant Utilization</h3>
          {loading ? (
            <div className="py-8 text-center text-[#8E85A0]"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : participants.length === 0 ? (
            <p className="text-sm text-center py-8 text-[#8E85A0]">
              No participants with authorized weekly hours yet. Set <em>Authorized Hours/Week</em> on a participant in the Participants page to start tracking.
            </p>
          ) : (
            <div className="space-y-3">
              {participants.map(p => (
                <div key={p.participant_id} className={`rounded-md border p-3 ${p.any_over ? 'border-[#D9534F] bg-[#FDECEA]/20' : p.any_near_limit ? 'border-[#FFB74D] bg-[#FFF3E0]/20' : 'border-[#DED8EB]'}`}
                  data-testid={`util-card-${p.participant_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-[#2D1B4E] text-sm">{p.name}</p>
                    {p.any_over && <Badge className="bg-[#FDECEA] text-[#D9534F] border border-[#D9534F] text-[10px]"><AlertTriangle className="h-2.5 w-2.5 inline mr-1" />Over Auth</Badge>}
                    {!p.any_over && p.any_near_limit && <Badge className="bg-[#FFF3E0] text-[#E65100] border border-[#FFB74D] text-[10px]">Near Limit</Badge>}
                    {!p.any_over && !p.any_near_limit && <Badge className="bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784] text-[10px]">Compliant</Badge>}                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(p.utilization || {}).filter(([, u]) => u.tracked).map(([sl, u]) => (
                      <div key={sl} className="space-y-0.5" data-testid={`util-block-${p.participant_id}-${sl}`}>
                        {/* Weekly */}
                        {u.auth_hours > 0 && (
                          <div className="flex items-center gap-3 text-xs" data-testid={`util-line-${p.participant_id}-${sl}`}>
                            <span className="font-medium text-[#2D1B4E] shrink-0" style={{ minWidth: 64, color: SERVICE_COLORS[sl] }}>{u.label}</span>
                            <span className="font-mono tabular-nums shrink-0" style={{ minWidth: 92 }}>
                              {u.used_hours}h / {u.auth_hours}h
                            </span>
                            <span className="font-mono tabular-nums opacity-70 shrink-0" style={{ minWidth: 80 }}>
                              {u.used_units}u / {u.auth_units}u
                            </span>
                            <span className="flex-1 min-w-[60px]"><UtilBar pct={u.pct} over={u.over} /></span>
                            <span className={`font-bold tabular-nums shrink-0 ${u.over ? 'text-[#D9534F]' : u.pct >= 85 ? 'text-[#E65100]' : 'text-[#5A4D6E]'}`} style={{ minWidth: 44, textAlign: 'right' }}>
                              {u.pct}%
                            </span>
                          </div>
                        )}
                        {/* Period */}
                        {u.period_active && (
                          <div className="flex items-center gap-3 text-[11px] pl-[68px]" data-testid={`period-line-${p.participant_id}-${sl}`}>
                            <span className="opacity-70 italic shrink-0" style={{ minWidth: 60 }}>Period:</span>
                            <span className="font-mono tabular-nums shrink-0" style={{ minWidth: 92 }}>
                              {u.period_used_hours}h / {u.period_auth_hours}h
                            </span>
                            <span className="font-mono tabular-nums opacity-70 shrink-0" style={{ minWidth: 80 }}>
                              {u.period_used_units}u / {u.period_auth_units}u
                            </span>
                            <span className="flex-1 min-w-[60px]"><UtilBar pct={u.period_pct} over={u.period_over} /></span>
                            <span className={`font-bold tabular-nums shrink-0 ${u.period_over ? 'text-[#D9534F]' : u.period_pct >= 85 ? 'text-[#E65100]' : 'text-[#5A4D6E]'}`} style={{ minWidth: 44, textAlign: 'right' }}>
                              {u.period_pct}%
                            </span>
                          </div>
                        )}
                        {u.period_active && (
                          <p className="text-[10px] text-[#8E85A0] pl-[68px]">
                            {u.period_start} → {u.period_end} · already utilized {u.already_utilized_hours}h ({u.already_utilized_units}u) external
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, accent, critical = false, testid }) {
  return (
    <Card className={`bg-white ${critical ? 'border-[#D9534F]' : 'border-[#DED8EB]'}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 mb-1">
          {critical ? <ShieldAlert className="h-3.5 w-3.5" style={{ color: accent }} /> : <ShieldCheck className="h-3.5 w-3.5" style={{ color: accent }} />}
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#5A4D6E]">{label}</p>
        </div>
        <p className="text-3xl font-bold font-mono" style={{ color: accent }}>{value}</p>
      </CardContent>
    </Card>
  );
}
